import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { useFinalResultsColumns } from '../hooks/useFinalResultsColumns';
import { useFlattenedFinalResults } from '../hooks/useFlattenedFinalResults';
import type { MappingResponse } from '../types';

/**
 * FinalResultsContent Component
 *
 * Displays the final flattened mapping results in a clean table format.
 * This is the content shown in the SECOND (confirm) step of the MappingWorkflowDialog.
 *
 * FEATURES:
 * - Flattens collapsible Source/Mapped rows into a single table
 * - Shows only essential columns (Type, ID, Name, Code, Vocabulary, Standard, Domain, Class)
 * - Clean, summary-focused presentation
 * - Client-side DataGrid (all data preloaded)
 * - Responsive column widths
 *
 * The difference from MappingResultsContent:
 * - No expandable rows (flattened structure)
 * - Fewer columns (no Status, Mapped Count, Reasoning)
 * - More compact visual presentation
 *
 * USAGE:
 * ```tsx
 * <FinalResultsContent mappingData={mappingData} />
 * ```
 */

interface FinalResultsContentProps {
  /** The mapping data to display in final format */
  mappingData: MappingResponse;
  /** Optional pre-computed flattened rows (for performance) */
  flatRows?: ReturnType<typeof useFlattenedFinalResults>;
  /** Optional pre-computed columns (for performance) */
  columns?: ReturnType<typeof useFinalResultsColumns>;
}

// No-op function for client-mode DataGrid
const noOpFetch = () => {
  // This is intentionally empty for client-mode DataGrid
};

export const FinalResultsContent = ({
  mappingData,
  flatRows: providedFlatRows,
  columns: providedColumns,
}: FinalResultsContentProps) => {
  const theme = useTheme();

  // Use provided flattened rows if available, otherwise compute them
  // This allows parent components to pass pre-computed data for efficiency
  const computedFlatRows = useFlattenedFinalResults(mappingData.results);
  const flatRows = providedFlatRows ?? computedFlatRows;

  // Use provided columns if available, otherwise compute them
  const computedColumns = useFinalResultsColumns();
  const columns = providedColumns ?? computedColumns;

  return (
    <Box sx={{ pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Results Table */}
      <Box sx={{ flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column' }}>
        <DataGridLayout id="final-results-datagrid-layout">
          <DataGrid
            id="final-results"
            mode="client"
            rows={flatRows}
            columns={columns}
            getRowId={(row) => row.rowId}
            useFetchDataGridData={noOpFetch}
            pageSizeOptions={[10, 25, 50, 100]}
            initialPageSize={25}
            pagination
            disableSearchParamSync
            sx={{
              '& .MuiDataGrid-cell': {
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.body2.fontWeight,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-row': {
                '&:nth-of-type(even)': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
              '& .MuiDataGrid-columnHeaders': {
                fontSize: theme.typography.subtitle1.fontSize,
                fontWeight: 700,
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                overflow: 'visible',
                whiteSpace: 'normal',
                lineHeight: theme.typography.subtitle1.lineHeight,
                textOverflow: 'unset',
              },
              '& .MuiDataGrid-footerContainer': {
                minHeight: 52,
                height: 52,
                '& .MuiTypography-root': {
                  color: theme.palette.text.primary,
                },
              },
              '& .MuiDataGrid-menuIconButton': {
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
            }}
            slotProps={{
              columnHeaders: {
                style: {
                  lineHeight: 'normal',
                  padding: '8px 16px',
                  fontWeight: 700,
                  fontSize: theme.typography.subtitle1.fontSize,
                },
              },
            }}
          />
        </DataGridLayout>
      </Box>
    </Box>
  );
};
